import {
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon
} from '@chakra-ui/core'
import { InputProps } from '@chakra-ui/core/dist/Input'
import { useField } from 'formik'
import * as React from 'react'
import { Text } from '../../../typography'

type NumericInputOptions = {
  allowNegative?: boolean
  allowDecimal?: boolean
}

/**
 * Clean a keystroke/paste into a partial number string, or return null to
 * reject it.
 *
 * The value stays a string while typing so in-between states such as "-",
 * "1." or "" survive; Yup turns it into a number at validation time, the same
 * way the mobile app does. A comma is read as the decimal point (field workers
 * type "1,5"), matching the backend's normalisation.
 */
export const sanitiseNumericInput = (
  raw: string,
  { allowNegative = true, allowDecimal = true }: NumericInputOptions = {}
): string | null => {
  const cleaned = raw.replace(/\s/g, '').replace(/,/g, '.')
  const pattern = new RegExp(`^${allowNegative ? '-?' : ''}\\d*${allowDecimal ? '\\.?\\d*' : ''}$`)
  return pattern.test(cleaned) ? cleaned : null
}

type ConnectedNumberInputProps = Omit<InputProps, 'min' | 'max'> & {
  label?: string
  name: string
  unit?: string
  suffix?: string
  /** Lower bound; only used to decide whether "-" may be typed. Range errors come from validation. */
  min?: number
  max?: number
  allowNegative?: boolean
  allowDecimal?: boolean
}

/**
 * A free-typing number field bound to Formik.
 *
 * This replaced Chakra 0.7's `NumberInput`, which made unbounded ("Unlimited")
 * fields unusable: it blocked the minus key outright, converted every
 * keystroke with `+value` (so "-" became NaN and an emptied field became 0),
 * and silently clamped out-of-range values on blur instead of reporting them.
 */
const ConnectedNumberInput: React.FC<ConnectedNumberInputProps> = ({
  label,
  unit,
  suffix,
  min,
  max,
  allowNegative,
  allowDecimal = true,
  ...rest
}) => {
  const [field, meta, helpers] = useField(rest.name)
  const { setValue, setTouched } = helpers
  // A field whose minimum is zero or more can never hold a negative value.
  const negativeAllowed = allowNegative ?? !(typeof min === 'number' && min >= 0)

  return (
    <Flex flexDirection="column" width="100%" mr={rest.mr} ml={rest.ml} mt={rest.mt} mb={rest.mb}>
      <FormControl isInvalid={!!(meta.touched && meta.error)}>
        {label && (
          <FormLabel htmlFor={field.name} fontWeight="normal" fontSize="sm">
            {label}
          </FormLabel>
        )}
        <InputGroup width="100%">
          {!!unit && <InputLeftAddon>{unit}</InputLeftAddon>}
          <Input
            id={field.name}
            name={field.name}
            type="text"
            inputMode={allowDecimal ? 'decimal' : 'numeric'}
            autoComplete="off"
            width="100%"
            fontWeight="bold"
            roundedLeft={unit ? 0 : 4}
            focusBorderColor="accent.500"
            placeholder={rest.placeholder}
            variant="filled"
            aria-valuemin={min}
            aria-valuemax={max}
            value={field.value !== undefined && field.value !== null ? `${field.value}` : ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const next = sanitiseNumericInput(event.target.value, {
                allowNegative: negativeAllowed,
                allowDecimal
              })
              if (next !== null) setValue(next)
            }}
            onBlur={() => setTouched(true)}
          />
          {suffix && (
            <InputRightAddon fontSize="sm" fontWeight="bold">
              {suffix}
            </InputRightAddon>
          )}
        </InputGroup>

        {meta.touched && meta.error ? (
          <Text color="red.500" fontSize="xs">
            {meta.error}
          </Text>
        ) : null}
      </FormControl>
    </Flex>
  )
}

export default ConnectedNumberInput

ConnectedNumberInput.defaultProps = {
  mb: 2,
  fontWeight: 'lighter'
}
